import {API} from '@/classes/api/api.class';
import {CreateToDoItemsRequestDto} from '@/classes/dto/to-do-manager/request/CreateToDoItems.request.dto';
import {ToDoItemTemplateDto} from '@/classes/dto/to-do-manager/request/templates/ToDoItem.template.dto';
import {LeanDocument, Types} from 'mongoose';
import {FindByIdOptions} from '@/interfaces/findByIdOptions';
import {FindToDoItemRequestDto} from '@/classes/dto/to-do-manager/request/FindToDoItem.request.dto';
import {UpdateToDoItemRequestDto} from '@/classes/dto/to-do-manager/request/UpdateToDoItem.request.dto';
import {CreateToDoTemplateRequestDto} from '@/classes/dto/to-do-manager/request/CreateToDoTemplate.request.dto';
import {ToDoItemDocument} from '@/documentTypes/to-do-manager/ToDoItem.document';
import {FindToDoTemplateRequestDto} from '@/classes/dto/to-do-manager/request/FindToDoTemplate.request.dto';
import {UpdateToDoTemplateRequestDto} from '@/classes/dto/to-do-manager/request/UpdateToDoTemplate.request.dto';
import {CompleteToDoItemRequestDto} from '@/classes/dto/to-do-manager/request/CompleteToDoItem.request.dto';
import {isUnknownRoute, probeFeature, PROBE_OBJECT_ID, silentRequest} from '@/helpers/featureProbe.helper';

export class ToDoManagerApi extends API {

    static service = 'to-do-manager';

    static items = {
        create: (data: CreateToDoItemsRequestDto): Promise<{
            ids: Types.ObjectId[],
            errors: Array<{ text: string, data: ToDoItemTemplateDto | Types.ObjectId }>
        }> => {
            return this.post(`items`, data);
        },
        findById: (id: Types.ObjectId, options?: FindByIdOptions): Promise<LeanDocument<ToDoItemDocument>> => {
            return this.get(`items/${id}`, options);
        },
        find: (data: FindToDoItemRequestDto): Promise<{ items: LeanDocument<ToDoItemDocument>[], maxPage: number }> => {
            return this.get(`items`, data);
        },
        update: (id: Types.ObjectId, data: UpdateToDoItemRequestDto): Promise<Types.ObjectId> => {
            return this.put(`items/${id}`, data);
        },
        complete: (id: Types.ObjectId, data: CompleteToDoItemRequestDto): Promise<Types.ObjectId> => {
            return this.put(`items/complete/${id}`, data);
        },
        remove: (id: Types.ObjectId): Promise<void> => {
            return this.delete(`items/${id}`);
        },
        /**
         * Notify v2: restores a soft-deleted to-do (within 30 days). Silent: never opens an error dialog,
         * throws an Error with `status` when it fails (use only after `supportsRestore()`).
         */
        restore: async (id: Types.ObjectId | string): Promise<boolean> => {
            const result = await silentRequest('put', this.service, `items/restore/${id}`, {});
            if (!result.ok) {
                throw Object.assign(new Error(result.message || 'Wiederherstellen fehlgeschlagen'), {status: result.status});
            }
            return true;
        },
        /**
         * true when DELETE is a soft delete that can be restored (new to-do-manager).
         * Probes `PUT items/restore/<unknown id>`: an old backend answers "Cannot PUT …" (unknown route).
         */
        supportsRestore: (): Promise<boolean> => probeFeature('toDoRestore', async () => {
            const result = await silentRequest('put', this.service, `items/restore/${PROBE_OBJECT_ID}`, {});
            // only answers that clearly come from the restore route count; network/5xx errors = not supported (safe)
            return result.ok || ([400, 403, 404, 409, 410, 422].includes(result.status || 0) && !isUnknownRoute(result));
        }),
    }

    static templates = {
        create: (data: CreateToDoTemplateRequestDto): Promise<Types.ObjectId> => {
            return this.post(`templates`, data);
        },
        findById: (id: Types.ObjectId, options: FindByIdOptions): Promise<LeanDocument<ToDoItemTemplateDto>> => {
            return this.get(`templates/${id}`, options);
        },
        find: (data: FindToDoTemplateRequestDto): Promise<{ templates: LeanDocument<ToDoItemDocument>[], maxPage: number }> => {
            return this.get(`templates`, data);
        },
        update: (id: Types.ObjectId, data: UpdateToDoTemplateRequestDto): Promise<Types.ObjectId> => {
            return this.put(`templates/${id}`, data);
        },
        remove: (id: Types.ObjectId): Promise<void> => {
            return this.delete(`templates/${id}`);
        },
    }
}
